import { useRef, useState, type ButtonHTMLAttributes, type ReactNode } from 'react'

/**
 * 非冪等な mutating action 用のボタン (double-click ガードの共通化)。
 *
 * `onClick` が Promise を返す場合、その完了までボタンを `disabled` にして
 * 連打による二重送信 (例: DELETE が 2 回飛ぶ / 通知が 2 件作られる) を防ぐ。
 * state 更新を待たずに弾けるよう、in-flight 判定は ref でも二重化している。
 *
 * 通常の同期ボタンには使わない (素の `<button>` で十分)。サーバ状態を変える
 * 非冪等処理を 1 クリックに制限したい箇所にだけ使う。
 */
export interface AsyncButtonProps extends Omit<ButtonHTMLAttributes<HTMLButtonElement>, 'onClick'> {
  /** クリックハンドラ。Promise を返すと、その解決までボタンが disabled になる。 */
  onClick: () => unknown
  children: ReactNode
}

export const AsyncButton = ({
  onClick,
  disabled,
  children,
  ...rest
}: AsyncButtonProps): JSX.Element => {
  const [isPending, setIsPending] = useState(false)
  const inFlightRef = useRef(false)

  const handleClick = async (): Promise<void> => {
    if (inFlightRef.current) return
    inFlightRef.current = true
    setIsPending(true)
    try {
      await onClick()
    } finally {
      inFlightRef.current = false
      setIsPending(false)
    }
  }

  return (
    <button
      type="button"
      {...rest}
      disabled={disabled === true || isPending}
      onClick={() => void handleClick()}
    >
      {children}
    </button>
  )
}
